import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';
import AnimatedBrandIcon from './AnimatedBrandIcon';
import MarcaTexto from './brand/MarcaTexto';
import { useFeature } from '../context/FeaturesContext';
import useOcultarAoRolar from '../hooks/useOcultarAoRolar';
import { MARKETPLACES, buscaPorMarketplace } from '../design/marketplaces';

const linkClasse = 'text-[14px] transition-colors hover:text-[var(--text)]';

/**
 * Uma coluna do rodapé.
 *
 * A partir de `sm` (640 px) é a coluna de sempre: título e lista. No celular a
 * mesma lista vira um acordeão: a linha do título é um botão de 52 px, com
 * aria-expanded/aria-controls, e o painel fechado fica fora do teclado e do
 * leitor de tela (visibility: hidden). O desenho e a animação ficam em
 * `.sv-rodape-*` (index.css). Mais de uma seção pode ficar aberta: fechar uma
 * enquanto outra abre fazia o conteúdo pular debaixo do dedo.
 */
function SecaoRodape({ id, titulo, aberta, alternar, children }) {
    const painelId = `rodape-painel-${id}`;
    return (
        <nav aria-label={titulo} className="sv-rodape-secao col-span-2 sm:col-span-1" data-testid={`footer-secao-${id}`}>
            <h2 className="sv-rodape-titulo-celular">
                <button
                    type="button"
                    className="sv-rodape-gatilho"
                    aria-expanded={aberta}
                    aria-controls={painelId}
                    onClick={alternar}
                    data-testid={`footer-acordeao-${id}`}
                >
                    <span>{titulo}</span>
                    <ChevronDown className="sv-rodape-chevron w-5 h-5" aria-hidden="true" />
                </button>
            </h2>
            <p className="sv-rodape-titulo text-[14px] font-bold mb-4" style={{ color: 'var(--text)' }}>{titulo}</p>
            <div id={painelId} className={`sv-rodape-painel ${aberta ? 'is-aberto' : ''}`} data-testid={`footer-painel-${id}`}>
                <div className="sv-rodape-painel-conteudo">
                    <ul className="flex flex-col gap-2.5">
                        {children}
                    </ul>
                </div>
            </div>
        </nav>
    );
}

export const Footer = () => {
    const showPlans = useFeature('show_plans');
    const [showTop, setShowTop] = useState(false);
    const [abertas, setAbertas] = useState({});
    const [rodapeNaTelaDoCelular, setRodapeNaTelaDoCelular] = useState(false);
    const rodapeRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setShowTop(window.scrollY > 400);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // No celular o rodapé já tem o próprio "Voltar ao topo": com ele na tela, o
    // botão redondo sai. Assim o rodapé não precisa de 128 px de respiro no fim.
    useEffect(() => {
        const rodape = rodapeRef.current;
        if (!rodape || typeof IntersectionObserver === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const celular = window.matchMedia('(max-width: 639px)');
        let naTela = false;
        const aplicar = () => setRodapeNaTelaDoCelular(naTela && celular.matches);
        const io = new IntersectionObserver(([entrada]) => { naTela = entrada.isIntersecting; aplicar(); });
        io.observe(rodape);
        if (celular.addEventListener) celular.addEventListener('change', aplicar);
        return () => {
            io.disconnect();
            if (celular.removeEventListener) celular.removeEventListener('change', aplicar);
        };
    }, []);

    // No celular o botão só aparece quando a pessoa sobe a página: descendo, ele
    // ficava por cima do preço e das curtidas dos cards da lista.
    const descendoNoCelular = useOcultarAoRolar({ limiar: 400 });

    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
    const alternar = (id) => () => setAbertas((atual) => ({ ...atual, [id]: !atual[id] }));

    return (
        <>
            {showTop && !descendoNoCelular && !rodapeNaTelaDoCelular && (
                <button onClick={scrollToTop}
                    className="fixed bottom-20 right-4 z-50 w-11 h-11 rounded-full flex items-center justify-center transition-transform hover:-translate-y-0.5"
                    style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action, #0A0A0A)', boxShadow: '0 10px 24px -10px rgba(0,0,0,0.6)' }}
                    aria-label="Voltar ao topo"
                    data-testid="scroll-to-top">
                    <ChevronUp size={20} />
                </button>
            )}

            <footer className="sv-footer mt-16" ref={rodapeRef} style={{ background: 'var(--bg-deep, var(--surface))', borderTop: '1px solid var(--border)' }}>
                {/* A partir de `sm` o rodapé termina com 128 px de respiro: o botão
                    redondo "voltar ao topo" fica fixo no canto de baixo, e sem esse
                    espaço ele parava em cima do link "Voltar ao topo" no fim da
                    página. No celular o botão redondo some com o rodapé na tela. */}
                <div className="site-container mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 pb-8 sm:pb-32">
                    <div className="grid grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr] gap-x-6 gap-y-0 sm:gap-y-10">
                        <div className="col-span-2 lg:col-span-1 max-w-sm mb-6 sm:mb-0">
                            <div className="flex items-center gap-2.5 mb-3 sm:mb-4">
                                <AnimatedBrandIcon size={30} animate={false} glow={false} />
                                <MarcaTexto className="text-[17px] font-bold tracking-wide" />
                            </div>
                            <p className="text-[14px] leading-relaxed" style={{ color: 'var(--muted)' }}>
                                Ofertas das maiores lojas do Brasil reunidas num lugar só, com o preço de antes e o de agora lado a lado.
                            </p>
                        </div>

                        <SecaoRodape id="lojas" titulo="Lojas" aberta={!!abertas.lojas} alternar={alternar('lojas')}>
                            {MARKETPLACES.map((m) => (
                                <li key={m.slug}>
                                    <Link to={buscaPorMarketplace(m.slug)} className={`${linkClasse} inline-flex items-center gap-2`} style={{ color: 'var(--muted)' }}>
                                        <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full" style={{ background: m.cor }} />
                                        {m.label}
                                    </Link>
                                </li>
                            ))}
                        </SecaoRodape>

                        <SecaoRodape id="navegacao" titulo="Navegação" aberta={!!abertas.navegacao} alternar={alternar('navegacao')}>
                            <li><Link to="/" className={linkClasse} style={{ color: 'var(--muted)' }} data-testid="footer-home">Início</Link></li>
                            {showPlans && (
                                <li><Link to="/planos" className={linkClasse} style={{ color: 'var(--muted)' }} data-testid="footer-planos">Planos</Link></li>
                            )}
                            <li><Link to="/sobre" className={linkClasse} style={{ color: 'var(--muted)' }} data-testid="footer-sobre">Sobre nós</Link></li>
                            <li><Link to="/downloads" className={linkClasse} style={{ color: 'var(--muted)' }} data-testid="footer-downloads">Baixar App</Link></li>
                            <li><Link to="/privacidade" className={linkClasse} style={{ color: 'var(--muted)' }} data-testid="footer-privacidade">Política de Privacidade</Link></li>
                        </SecaoRodape>

                        <SecaoRodape id="suporte" titulo="Suporte" aberta={!!abertas.suporte} alternar={alternar('suporte')}>
                            <li><Link to="/termos" className={linkClasse} style={{ color: 'var(--muted)' }} data-testid="footer-termos">Termos de Uso</Link></li>
                            <li><Link to="/exclusao-de-dados" className={linkClasse} style={{ color: 'var(--muted)' }} data-testid="footer-exclusao">Exclusão de Dados</Link></li>
                            <li><a href="mailto:contato@supervitrineofertas.com" className={linkClasse} style={{ color: 'var(--muted)' }} data-testid="footer-contato">Contato</a></li>
                            <li><Link to="/login" className={linkClasse} style={{ color: 'var(--muted)' }} data-testid="footer-login">Entrar</Link></li>
                        </SecaoRodape>
                    </div>

                    <div className="sv-rodape-base sm:mt-12 pt-1 sm:pt-6 flex flex-col-reverse sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4" style={{ borderTop: '1px solid var(--border)' }}>
                        <p className="text-[13px]" style={{ color: 'var(--muted-2)' }}>
                            © {new Date().getFullYear()} SuperVitrine. Todos os direitos reservados.
                        </p>
                        <button onClick={scrollToTop} className="sv-rodape-topo inline-flex items-center gap-1 text-[13px] font-semibold transition-colors hover:text-[var(--text)]"
                            style={{ color: 'var(--sv-gold-primary)' }} data-testid="footer-scroll-top">
                            <ChevronUp size={16} /> Voltar ao topo
                        </button>
                    </div>
                </div>
            </footer>
        </>
    );
};
